/**
 * The full-screen picture — the viewer's third action (D-032).
 *
 * The design authority, 2026-09-01: *"give users the ability to expand the
 * picture to full screen in addition to Retaking or Deleting them."*
 *
 * A picture over the whole viewport, as close as the surveyor wants it: pinch
 * to zoom, drag to pan, double-tap in and out. The Fullscreen API is not what
 * this is — iOS offers it to `<video>` alone, and the installed PWA already
 * owns the screen — so it is an overlay, the app's third true overlay after
 * the map sheet and the viewer, built the same way: `role="dialog"
 * aria-modal`, focus on open, Escape closes, and one labelled 44px control
 * (brief §2) that does the closing. Nothing else is offered here: Retake and
 * Delete are decisions, and the viewer underneath is where decisions are made.
 *
 * The geometry is `src/ui/state/photoZoom.ts`, pure and unit-tested; this
 * component only tracks fingers and applies the transform it is handed. The
 * `src` is the ORIGINAL's object URL once the viewer has it (round 8.1, task
 * 4: the shelf's cards draw a 640-px thumbnail, and the viewer resolves the
 * full picture on open) and the thumbnail's until then; when the prop
 * changes the `<img>` simply reloads, and `frame()` reads the new natural
 * size on the next gesture.
 *
 * Round 8 task 5: the closing control moved off a floating dark circle (it
 * sat over the navy top bar in her own photos and read as part of the
 * picture) onto a full-width strip, `.pf__bar` — the caption at its left, an
 * amber "Done" pill at its right, still the same accessible name. A straight
 * drag down at 1× — where there is nowhere left to pan to, `clampPan` holds
 * the picture at (0, 0) — reads as "let go of this" rather than a failed
 * pan, so it closes the overlay too; the same drag at anything past 1× still
 * pans, because there the picture actually has somewhere to go.
 */

import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';

import {
  IDENTITY,
  dismissDrag,
  fitContain,
  panBy,
  pinchTo,
  settle,
  toggleTapZoom,
  type Point,
  type ZoomState,
} from '../state/photoZoom';

export interface PhotoFullscreenProps {
  src: string;
  label: string;
  onClose: () => void;
}

type Gesture =
  /**
   * `origin` is where the finger landed and `last` where it was one event
   * ago. The tap-or-drag decision is made against ORIGIN — pointer events
   * arrive at the frame rate, so a slow, deliberate drag (a surveyor reading
   * a zoomed breaker label) moves only a pixel or two per event and would
   * never clear the slop if each event were judged on its own.
   */
  | { kind: 'pan'; origin: Point; last: Point; moved: boolean }
  | { kind: 'pinch'; start: ZoomState; startDist: number; startMid: Point };

/** Two taps this close together, in time and place, are one double tap. */
const DOUBLE_TAP_MS = 300;
const DOUBLE_TAP_PX = 24;
/** A drag under this is still a tap — a thumb wobbles; iOS's own slop is ~10. */
const TAP_SLOP_PX = 8;

export function PhotoFullscreen({ src, label, onClose }: PhotoFullscreenProps): ReactNode {
  const [zoom, setZoom] = useState<ZoomState>({ ...IDENTITY });
  const stage = useRef<HTMLDivElement | null>(null);
  const image = useRef<HTMLImageElement | null>(null);
  const closeButton = useRef<HTMLButtonElement | null>(null);
  const pointers = useRef(new Map<number, Point>());
  const gesture = useRef<Gesture | null>(null);
  const lastTap = useRef<{ at: number; point: Point } | null>(null);
  const current = useRef<ZoomState>(zoom);
  current.current = zoom;
  // The parent re-creates `onClose` on every render; read it through a ref so
  // neither effect below re-runs — a re-run would re-focus ✕ mid-gesture.
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    closeButton.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopImmediatePropagation();
        close.current();
      }
    };
    window.addEventListener('keydown', onKey, { capture: true });
    return () => window.removeEventListener('keydown', onKey, { capture: true });
  }, []);

  /**
   * The stage's box, and the picture as the stage shows it at 1× — its
   * natural size contain-fitted into the box (the `<img>` fills the stage;
   * the picture inside it is letterboxed on one axis). Natural size is what
   * matters: the element's own box is the whole stage, and a pan must stop
   * at the PICTURE's edge, not the letterbox's.
   */
  const frame = (): { width: number; height: number; image?: { width: number; height: number } } => {
    const box = stage.current?.getBoundingClientRect();
    const view = { width: box?.width ?? window.innerWidth, height: box?.height ?? window.innerHeight };
    const img = image.current;
    if (!img) return view;
    return { ...view, image: fitContain({ width: img.naturalWidth, height: img.naturalHeight }, view) };
  };

  /** A pointer, relative to the stage's centre — the transform origin. */
  const relative = (event: { clientX: number; clientY: number }): Point => {
    const box = stage.current?.getBoundingClientRect();
    if (!box) return { x: event.clientX, y: event.clientY };
    return { x: event.clientX - box.left - box.width / 2, y: event.clientY - box.top - box.height / 2 };
  };

  const distance = (a: Point, b: Point): number => Math.hypot(a.x - b.x, a.y - b.y);
  const midpoint = (a: Point, b: Point): Point => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>): void => {
    stage.current?.setPointerCapture(event.pointerId);
    const point = relative(event);
    pointers.current.set(event.pointerId, point);
    const all = [...pointers.current.values()];
    if (all.length === 1) {
      gesture.current = { kind: 'pan', origin: point, last: point, moved: false };
    } else if (all.length === 2) {
      const [a, b] = all as [Point, Point];
      gesture.current = { kind: 'pinch', start: current.current, startDist: distance(a, b), startMid: midpoint(a, b) };
    }
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>): void => {
    if (!pointers.current.has(event.pointerId)) return;
    const point = relative(event);
    pointers.current.set(event.pointerId, point);
    const active = gesture.current;
    if (!active) return;
    if (active.kind === 'pinch') {
      const all = [...pointers.current.values()];
      if (all.length < 2) return;
      const [a, b] = all as [Point, Point];
      setZoom(pinchTo(active.start, active.startDist, active.startMid, distance(a, b), midpoint(a, b), frame()));
      return;
    }
    // At 1× a mostly-downward drag has nowhere to pan to — `clampPan` holds it
    // at (0, 0) — so it is read as "let go", not a failed pan (`dismissDrag`,
    // photoZoom.ts, which also refuses a sideways swipe that merely sags).
    // Checked against ORIGIN, not the last event, the same way the tap-vs-drag
    // decision below is: a slow drag moves a pixel or two per event and would
    // never cross a per-event threshold.
    if (dismissDrag(active.origin, point, current.current.scale)) {
      close.current();
      return;
    }
    if (!active.moved) {
      const fromOrigin = { x: point.x - active.origin.x, y: point.y - active.origin.y };
      if (Math.hypot(fromOrigin.x, fromOrigin.y) > TAP_SLOP_PX) {
        active.moved = true;
        // The distance covered while still deciding is not thrown away.
        setZoom((state) => panBy(state, fromOrigin.x, fromOrigin.y, frame()));
      }
    } else {
      const dx = point.x - active.last.x;
      const dy = point.y - active.last.y;
      setZoom((state) => panBy(state, dx, dy, frame()));
    }
    active.last = point;
  };

  const onPointerEnd = (event: ReactPointerEvent<HTMLDivElement>): void => {
    if (!pointers.current.has(event.pointerId)) return;
    const point = relative(event);
    pointers.current.delete(event.pointerId);
    const active = gesture.current;
    const remaining = [...pointers.current.values()];

    if (remaining.length === 1) {
      // One finger lifted from a pinch: carry on as a pan with the other.
      const left = remaining[0] as Point;
      gesture.current = { kind: 'pan', origin: left, last: left, moved: true };
      return;
    }
    if (remaining.length > 1) return;

    gesture.current = null;
    if (active?.kind === 'pan' && !active.moved && event.type !== 'pointercancel') {
      const previous = lastTap.current;
      const now = event.timeStamp;
      if (previous && now - previous.at < DOUBLE_TAP_MS && distance(previous.point, point) < DOUBLE_TAP_PX) {
        lastTap.current = null;
        setZoom((state) => toggleTapZoom(state, point, frame()));
        return;
      }
      lastTap.current = { at: now, point };
    }
    setZoom((state) => settle(state));
  };

  return (
    <div className="pf" role="dialog" aria-modal="true" aria-label={`Full screen — ${label}`}>
      <div
        className="pf__stage"
        ref={stage}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerEnd}
        onPointerCancel={onPointerEnd}
      >
        <img
          ref={image}
          className="pf__img"
          src={src}
          alt={label}
          draggable={false}
          style={{ transform: `translate(${String(zoom.x)}px, ${String(zoom.y)}px) scale(${String(zoom.scale)})` }}
        />
      </div>
      <div className="pf__bar">
        <p className="pf__cap" data-legibility="helper">
          {label}
        </p>
        <button
          type="button"
          className="pf__close btn btn--primary"
          aria-label="Close full screen"
          ref={closeButton}
          onClick={onClose}
        >
          Done
        </button>
      </div>
    </div>
  );
}
